import {useQuery} from '@aboutyou/data-fetcher';
import {useIsStorageConsentGiven} from '@aboutyou/storage';
import {doAsync, localStorageRemoveItem} from '@aboutyou/utils';
import {USER_OPTIONAL_CACHE_KEY} from '../../CacheKeys';
import {
  getUserDataFromLocalStorage,
  setLocalStorageDataWithEncoding,
} from '../../utils/read-write-user-data';
import {BroadcastUserDataToTabs} from '../SyncSessionBetweenTabs';
import {createDataStore} from '../createDataStore';
import {TADARIDA_OPTIONAL_USER_PERSIST_KEY} from './constants';
import {OptionalUserState} from './types';

const useExpensiveOptionalUser = () => {
  const isStorageConsentGiven = useIsStorageConsentGiven();

  const query = useQuery(
    USER_OPTIONAL_CACHE_KEY,
    async (): Promise<OptionalUserState> => {
      const dataFromLocalStorage =
        getUserDataFromLocalStorage<OptionalUserState>({
          storageKey: TADARIDA_OPTIONAL_USER_PERSIST_KEY,
        });
      if (dataFromLocalStorage) {
        return dataFromLocalStorage;
      }

      return {userData: null};
    },
  );

  const saveData = (data: OptionalUserState | undefined) => {
    BroadcastUserDataToTabs.broadcastData({
      key: TADARIDA_OPTIONAL_USER_PERSIST_KEY,
      data,
    });

    if (data) {
      query.updateCache(data);
      if (isStorageConsentGiven) {
        doAsync(() => {
          setLocalStorageDataWithEncoding({
            storageKey: TADARIDA_OPTIONAL_USER_PERSIST_KEY,
            data,
          });
        });
      }
    }
  };

  return {...query, updateCache: saveData};
};

export const cleanUpOptionalDataState = () =>
  localStorageRemoveItem(TADARIDA_OPTIONAL_USER_PERSIST_KEY);

const {Provider, useData: useOptionalUser} = createDataStore<OptionalUserState>(
  {
    defaultValue: {
      userData: null,
    },
    useQueryHook: useExpensiveOptionalUser,
  },
);

export {Provider as OptionalUserProvider, useOptionalUser};
